import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import FormHeader from "./form-header";
import { useContext, useMemo, useState } from "react";
import { isEmpty, isFunction } from "lodash";
import DocumentGeneratorCTX from "../../context";

const SUPPORTED_DOCUMENTS_TYPES = Object.freeze({
  MAGAZINE: "MAGAZINE",
  ADDRESS_BOOK: "ADDRESS_BOOK",
});

const SUPPORTED_DOCUMENTS = {
  [SUPPORTED_DOCUMENTS_TYPES.MAGAZINE]: {
    type: SUPPORTED_DOCUMENTS_TYPES.MAGAZINE,
    label: "Magazine Pages",
  },
  [SUPPORTED_DOCUMENTS_TYPES.ADDRESS_BOOK]: {
    type: SUPPORTED_DOCUMENTS_TYPES.ADDRESS_BOOK,
    label: "Address Books",
  },
};

const CALENDAR_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const NO_OF_GENERATED_YEARS = 3;

const getSelectableYears = () => {
  const todayDate = new Date();
  const lastYear = todayDate.getFullYear() - 1;
  const yearOptions = [];
  for (let i = 0; i < NO_OF_GENERATED_YEARS; i++) {
    yearOptions.push(lastYear + i);
  }
  return yearOptions;
};

const GenerationCriteriaForm = ({
  documentType,
  setDocumentType,
  dataset,
  setDataset,
  selectedYear,
  setSelectedYear,
  selectedMonth,
  setSelectedMonth,
  pagesStartFrom,
  setPagesStartFrom,
  didSubmitGenerateBtn,
  setDidSubmitGenerateBtn,
}) => {
  const selectableYears = getSelectableYears();
  const { printGeneratedContent } = useContext(DocumentGeneratorCTX);
  const [contentGenerated, setContentGenerated] = useState(false);

  const isSubmitBtnDisabled = useMemo(() => {
    if (didSubmitGenerateBtn) return true;
    if (isEmpty(documentType)) {
      return true;
    }
    if (documentType === SUPPORTED_DOCUMENTS_TYPES.ADDRESS_BOOK) {
      return isEmpty(dataset);
    }
    if (documentType === SUPPORTED_DOCUMENTS_TYPES.MAGAZINE) {
      return (
        isEmpty(selectedYear) ||
        isEmpty(selectedMonth) ||
        isEmpty(pagesStartFrom)
      );
    }
    return false;
  }, [
    didSubmitGenerateBtn,
    dataset,
    documentType,
    pagesStartFrom,
    selectedMonth,
    selectedYear,
  ]);

  const handleFileUpload = (event) => {
    const uploadedFile = (event.target?.files ?? [])[0];
    if (uploadedFile instanceof File) {
      const fileReader = new FileReader();
      fileReader.readAsText(uploadedFile, "UTF-8");
      fileReader.onload = (event) => {
        if (event.target.result) {
          try {
            const dataset = JSON.parse(event.target.result);
            setDataset(dataset);
          } catch (error) {
            console.log(">>>> error ", error);
          }
        }
      };
    }
  };

  const handleReset = () => {
    setDocumentType("");
    setSelectedYear("");
    setSelectedMonth("");
    setPagesStartFrom(0);
    didSubmitGenerateBtn(false);
  };

  return (
    <Paper className="generation-criteria-form">
      <Grid container direction="column">
        <FormHeader />
        <Grid item container direction="column" padding={2} rowGap={2}>
          <FormControl disabled={didSubmitGenerateBtn}>
            <FormLabel>Select Document Type</FormLabel>
            <RadioGroup
              value={documentType}
              onChange={(event, value) => {
                event.preventDefault();
                setDocumentType(value);
              }}
            >
              {Object.values(SUPPORTED_DOCUMENTS).map((current) => (
                <FormControlLabel
                  key={current.type}
                  value={current.type}
                  label={current.label}
                  control={
                    <Radio size="small" disabled={didSubmitGenerateBtn} />
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
          <FormControl disabled={isEmpty(documentType) || didSubmitGenerateBtn}>
            <FormLabel>Upload Dataset</FormLabel>
            <TextField
              type="file"
              variant="outlined"
              fullWidth
              inputProps={{ accept: "application/json" }}
              onChange={handleFileUpload}
              size="small"
              disabled={isEmpty(documentType) || didSubmitGenerateBtn}
            ></TextField>
          </FormControl>
          {documentType === SUPPORTED_DOCUMENTS_TYPES.MAGAZINE && (
            <Grid item container direction="row" columnGap={5} rowGap={1}>
              <Grid item xs>
                <FormControl
                  fullWidth
                  disabled={isEmpty(dataset) || didSubmitGenerateBtn}
                >
                  <FormLabel>Year</FormLabel>
                  <Select
                    size="small"
                    value={selectedYear}
                    onChange={(event) => {
                      const selectedYear = event.target.value;
                      setSelectedYear(selectedYear.toString());
                    }}
                    disabled={isEmpty(dataset) || didSubmitGenerateBtn}
                  >
                    {selectableYears.map((current) => (
                      <MenuItem key={current} value={current}>
                        {current}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs>
                <FormControl
                  fullWidth
                  disabled={isEmpty(selectedYear) || didSubmitGenerateBtn}
                >
                  <FormLabel>Month</FormLabel>
                  <Select
                    size="small"
                    value={selectedMonth}
                    onChange={(event) => {
                      const selectedMonth = event.target.value;
                      setSelectedMonth(selectedMonth);
                    }}
                    disabled={isEmpty(selectedYear) || didSubmitGenerateBtn}
                  >
                    {CALENDAR_MONTHS.map((current) => (
                      <MenuItem key={current} value={current.toString()}>
                        {current}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  disabled={isEmpty(selectedMonth) || didSubmitGenerateBtn}
                >
                  <FormLabel>Pages Start From</FormLabel>
                  <TextField
                    value={pagesStartFrom}
                    onChange={(event) => {
                      setPagesStartFrom(event.target.value);
                    }}
                    type="number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={isEmpty(selectedMonth) || didSubmitGenerateBtn}
                    InputProps={{ accept: "image/*" }}
                  ></TextField>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <Grid item container direction="row" columnGap={1}>
            {didSubmitGenerateBtn && contentGenerated ? (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  if (isFunction(printGeneratedContent)) {
                    printGeneratedContent();
                  }
                }}
                startIcon={<DownloadIcon size={14} />}
              >
                Print Document
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setDidSubmitGenerateBtn(true);
                  setTimeout(() => {
                    setContentGenerated(true);
                  }, 5000);
                }}
                disabled={isSubmitBtnDisabled}
                startIcon={
                  didSubmitGenerateBtn ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AssignmentIcon />
                  )
                }
              >
                Generate Document
              </Button>
            )}
            <Button variant="outlined" onClick={handleReset} color="error">
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GenerationCriteriaForm;
